// IncomingWebhookSetupModal.jsx

import React from 'react';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import ModalActions from './ModalActions';

/**
 * IncomingWebhookSetupModal
 *
 * Renders setup details for an existing incoming webhook.
 *
 * @param {object} theme - Active theme palette values.
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} onClose - Handler to close the modal.
 * @param {string} selectedIncomingHookUrl - Selected webhook url.
 * @param {string} selectedIncomingHeaderPreview - Header template for selected webhook.
 * @param {array} selectedIncomingFieldRows - Payload field rows available for selected type.
 * @param {string} incomingSecretHeaderName - Required incoming secret header name.
 * @param {function} onCopyToClipboard - Handler to copy values.
 *
 * @returns {ReactNode} The rendered incoming webhook setup modal.
 */
const IncomingWebhookSetupModal = ({
    theme,
    isOpen,
    onClose,
    selectedIncomingHookUrl,
    selectedIncomingHeaderPreview,
    selectedIncomingFieldRows,
    incomingSecretHeaderName,
    onCopyToClipboard,
}) => {

    // ----- RENDER ----- //

    return (
        <Modal
            title="Webhook Setup"
            isOpen={isOpen}
            onClose={onClose}
            width="620px"
            maxHeight="85vh"
        >
            <div className="api-integrations-modal-body">
                {/* INTRO */}
                <div
                    className="api-integrations-subtitle"
                    style={{
                        color: theme.foregroundMuted,
                    }}
                >
                    Use this reference when wiring tools like Zapier. Existing webhook secrets are never re-shown.
                </div>

                {/* DETAILS */}
                <div className="api-integrations-form-grid">
                    <div
                        className="api-integrations-field-label"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Webhook URL
                    </div>
                    <div
                        className="api-integrations-static-field"
                        style={{
                            color: theme.foreground,
                        }}
                    >
                        {selectedIncomingHookUrl || 'Unavailable'}
                    </div>

                    <div
                        className="api-integrations-field-label"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Required Header
                    </div>
                    <div
                        className="api-integrations-static-field api-integrations-code"
                        style={{
                            color: theme.foreground,
                        }}
                    >
                        {selectedIncomingHeaderPreview}
                    </div>
                </div>

                {/* PAYLOAD FIELD MAP */}
                <div
                    className="api-integrations-field-map"
                    style={{
                        borderColor: theme.backgroundMuted,
                    }}
                >
                    <div
                        className="api-integrations-field-map-title"
                        style={{
                            color: theme.foreground,
                        }}
                    >
                        Available Payload Fields
                    </div>

                    {selectedIncomingFieldRows.length === 0 && (
                        <div
                            className="api-integrations-empty"
                            style={{
                                color: theme.foregroundMuted,
                            }}
                        >
                            No fields found for this type.
                        </div>
                    )}

                    {selectedIncomingFieldRows.map((field) => (
                        <div
                            key={field.fieldId}
                            className="api-integrations-field-map-row"
                            style={{
                                borderColor: theme.backgroundMuted,
                            }}
                        >
                            <div
                                className="api-integrations-field-map-slug"
                                style={{
                                    color: theme.foreground,
                                }}
                            >
                                {field.slug}
                            </div>
                            <div
                                className="api-integrations-field-map-meta"
                                style={{
                                    color: theme.foregroundMuted,
                                }}
                            >
                                {field.label} ({field.type})
                            </div>
                        </div>
                    ))}
                </div>

                {/* ACTIONS */}
                <ModalActions>
                    <Button
                        label="Copy URL"
                        onClick={() => onCopyToClipboard(selectedIncomingHookUrl, 'Incoming webhook URL copied.')}
                        enabled={Boolean(selectedIncomingHookUrl)}
                        height="30px"
                        fontSize="12px"
                        borderRadius="6px"
                    />
                    <Button
                        label="Copy Header"
                        onClick={() => onCopyToClipboard(incomingSecretHeaderName, 'Incoming header name copied.')}
                        enabled={Boolean(incomingSecretHeaderName)}
                        height="30px"
                        fontSize="12px"
                        borderRadius="6px"
                    />
                    <Button
                        label="Copy Header Template"
                        onClick={() => onCopyToClipboard(selectedIncomingHeaderPreview, 'Incoming header template copied.')}
                        enabled={Boolean(selectedIncomingHeaderPreview)}
                        height="30px"
                        fontSize="12px"
                        borderRadius="6px"
                    />
                </ModalActions>
            </div>
        </Modal>
    );
};

// Dev Tools
IncomingWebhookSetupModal.displayName = 'IncomingWebhookSetupModal';

export default IncomingWebhookSetupModal;
